import React, { createContext, useContext } from 'react';
import type { ReactNode } from 'react';
import type { SiteConfig } from '@/types/sites';

/**
 * Public interface describing the shape of the site configuration context.
 */
interface SiteConfigContextType {
  config: SiteConfig;
}

const SiteConfigContext = createContext<SiteConfigContextType | undefined>(undefined);

interface SiteConfigProviderProps {
  children: ReactNode;
  config: SiteConfig;
}

/**
 * Provider that makes the resolved {@link SiteConfig} available throughout the
 * application tree.
 */
export const SiteConfigProvider: React.FC<SiteConfigProviderProps> = ({
  children,
  config
}) => {
  return (
    <SiteConfigContext.Provider value={{ config }}>
      {children}
    </SiteConfigContext.Provider>
  );
};

export const useSiteConfig = () => {
  const context = useContext(SiteConfigContext);
  if (context === undefined) {
    throw new Error('useSiteConfig must be used within a SiteConfigProvider');
  }
  return context;
};

// Safe version that returns null if not in provider context
/**
 * Variant of {@link useSiteConfig} that returns `null` instead of throwing
 * when used outside of the provider. Useful for optional dependencies such as
 * analytics wrappers.
 */
export const useSiteConfigSafe = () => {
  const context = useContext(SiteConfigContext);
  return context || null;
};

export default SiteConfigContext;